import { Component, OnInit, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { faqData, faqRPData, faqCategories, faqPartnerCategories, faqRPNewData } from '../faq-data';
import { isPartner } from '@app/helpers/mode.helpers';
import { Subscription } from 'rxjs';
import { ServiceConfigService } from '@app/services/service-config.service';

interface FaqQuestion {
  key: string;
  value: string;
  show: boolean;
}

interface FaqCategory {
  id: string;
  title: string;
  icon: string;
  questionKeyList?: string[];
  header?: string;
}

@Component({
  selector: 'faq-questions',
  templateUrl: './faq-questions.component.html',
  styleUrls: ['./faq-questions.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class FaqQuestionsComponent implements OnInit, OnDestroy {
  private readonly medicalAppealQuestionKeys = ['FAQ_KEY_45', 'FAQ_KEY_46'];
  categoryId: string = '';
  category: FaqCategory | null = null;
  questions: FaqQuestion[] = [];
  isPartner: boolean = false;
  userType: string = '';
  faqRevampSwitch = false;
  medicalAppealsSwitch = false;
  currentLang: string;
  
  private routeSub: Subscription;
  private querySub: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public translateService: TranslateService,
    public serverConfig: ServiceConfigService
  ) {}

  ngOnInit(): void {
    this.faqRevampSwitch = this.serverConfig.getServerConfig()?.faqRevampSwitch;
    this.medicalAppealsSwitch = this.serverConfig.getServerConfig()?.medicalAppealsSwitch;
    this.currentLang = sessionStorage.getItem('currentLang') || 'en';

    if (sessionStorage.getItem('userType')) {
      this.userType = sessionStorage.getItem('userType');
      this.isPartner = isPartner(this.userType);
    }

    this.routeSub = this.route.params.subscribe(params => {
      this.categoryId = params['categoryId'];
      this.loadCategoryQuestions();
    });

    this.querySub = this.route.queryParams.subscribe(params => {
      if (params.lang === 'en' || params.lang === 'sp') {
        this.translateService.setDefaultLang(params.lang);
      }
      if (params.usertype) {
        this.userType = params.usertype;
        this.isPartner = isPartner(this.userType);
        this.loadCategoryQuestions();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
    if (this.querySub) {
      this.querySub.unsubscribe();
    }
  }

  loadCategoryQuestions(): void {
    const categories = this.isPartner ? faqPartnerCategories : faqCategories;
    const data = this.isPartner ? (this.faqRevampSwitch ? faqRPNewData : faqRPData) : faqData;

    this.category = categories.find(c => c.id === this.categoryId) || null;
    
    if (!this.category) {
      this.router.navigate(['/faq']);
      return;
    }

    const allQuestions = data.reduce((acc, item) => acc.concat(item.questions || []), []);
    this.questions = allQuestions.filter(q => 
      this.category.questionKeyList?.includes(q.key)
    ).sort((a, b) =>
      this.category.questionKeyList?.indexOf(a.key) - this.category.questionKeyList?.indexOf(b.key)
    );

    if (!this.medicalAppealsSwitch) {
      this.questions = this.questions.filter(q => !this.medicalAppealQuestionKeys.includes(q.key));
    }

    this.questions.forEach(q => q.show = true);
  }

  showHide(item: FaqQuestion): void {
    item.show = !item.show;
  }

  goBackToCategories(): void {
    this.router.navigate(['/faq'], {
      queryParamsHandling: 'preserve'
    });
  }

  isAppealCategory(): boolean {
    return this.categoryId === 'appeal-faqs';
  }

  isEligibilityAppealsHeader(questionKey: string): boolean {
    return this.isAppealCategory() && questionKey === 'FAQ_KEY_27';
  }

  isMedicalAppealsHeader(questionKey: string): boolean {
    return this.isAppealCategory() && questionKey === 'FAQ_KEY_45';
  }

  formatFaqAnswer(translatedValue: string): string {
    if (!translatedValue || !this.isArabicOrPersianLang()) {
      return translatedValue;
    }
    return translatedValue.replace(/888-345-5575/g, '5575-345-888');
  }

  isArabicOrPersianLang(): boolean {
    const selectedLang = sessionStorage.getItem('globalTranslatedLan') || sessionStorage.getItem('currentLang') || 'en';
    return selectedLang === 'ar' || selectedLang === 'fa';
  }

}
